import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  StepperContent,
  StepperHeader,
  StepperSubmit
} from '../../../../../shared/general/stepper';
import { utilsHomeValueGetDefaultValuesStep1 } from '../../utils';
import { DynamicInput, FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorsHomeValueFormFields,
  CalculatorsHomeValueResult,
  CalculatorsHomeValueStep1FormFields
} from '../../../../../../@types/features/calculators';
import { getQueryParam } from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCalculatorHomeValueStep1FormFields } from '../../config';
import { homeValueUseResidenceValueHook } from '../../hooks';
import { validationMessagesTranslated } from '../../../../../shared/validation/validation-messages';
import { Heading } from '@chakra-ui/react';
import { isValidZipcode } from '../../../../../../utils/validations';

export const CalculatorHomeValueStepperStep1: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const calculatorHomeValueStep1FormFields = useCalculatorHomeValueStep1FormFields(intl);
  const [completedText, setCompletedText] = useState('');
  const { busy, error, extensions, getAdressInformation, setError, getPropertyInformation } =
    homeValueUseResidenceValueHook();
  const { updateForm, isLoading, setLoading } = useFormContext<
    CalculatorsHomeValueResult,
    CalculatorsHomeValueFormFields
  >();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsHomeValueGetDefaultValuesStep1(intl)
  });

  const { postalCode, houseNumber } = watch();

  const onSubmit = async (formData: Record<keyof CalculatorsHomeValueStep1FormFields, string>) => {
    setLoading(true);
    formData.postalCode = formData.postalCode.replace(/ /g, '');
    setCompletedText(`${formData.postalCode} ${formData.houseNumber} ${formData.houseNumberExt}`);
    await getPropertyInformation(
      formData.postalCode,
      formData.houseNumber,
      formData.houseNumberExt
    );
    updateForm(formData);
    setLoading(false);
  };

  useEffect(() => {
    setError(null);
    if (postalCode && houseNumber && isValid) {
      setValue('houseNumberExt', '', { shouldValidate: false });
      const timeout = setTimeout(() => getAdressInformation(postalCode, houseNumber), 1000);
      return () => clearTimeout(timeout);
    }
  }, [postalCode, houseNumber, isValid]);

  useEffect(() => {
    if (extensions.length > 1) {
      setValue('houseNumberExt', extensions[0].value, { shouldValidate: false });
    } else {
      setValue('houseNumberExt', '', { shouldValidate: false });
    }
  }, [setValue, extensions]);

  useEffect(() => {
    const postalCode = getQueryParam('postcode');
    setValue('postalCode', postalCode ? postalCode : '', { shouldValidate: false });
  }, [setValue]);

  const handleCustomEvent = (event: Event) => {
    const customEvent = event as CustomEvent; // Cast Event to CustomEvent
    setValue('postalCode', customEvent.detail.answer, { shouldValidate: true });
  };

  useEffect(() => {
    window.addEventListener('homeValueMeter', handleCustomEvent);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('homeValueMeter', handleCustomEvent);
    };
  }, []);

  return (
    <Fragment>
      <StepperHeader
        onClose={handleSubmit(onSubmit)}
        title={title}
        id={id}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Heading size={`lg`}>
            <FormattedMessage
              id={`step1.header.title`}
              defaultMessage={`Wat is mijn huis waard?`}
            />
          </Heading>

          <Heading size={`md`}>
            <FormattedMessage
              id={`step1.property.title`}
              defaultMessage={`Wat is het adres van de woning?`}
            />
          </Heading>
          <div className={`flex flex-col md:flex-row md:gap-10`}>
            <div className={`${extensions.length > 1 ? 'md:basis-2/5' : 'md:basis-1/2'}`}>
              <FormElementDynamicInputTyped<CalculatorsHomeValueStep1FormFields>
                formFields={calculatorHomeValueStep1FormFields}
                id={'postalCode'}
                register={register}
                validation={{
                  required: validationMessagesTranslated(intl).required,
                  validate: (value: string) =>
                    isValidZipcode(value, validationMessagesTranslated(intl).zipCode)
                }}
                errors={errors}
                values={watch()}
                options={{ required: true }}
              />
            </div>
            <div className={`${extensions.length > 1 ? 'md:basis-2/5' : 'md:basis-1/2'}`}>
              <FormElementDynamicInputTyped<CalculatorsHomeValueStep1FormFields>
                formFields={calculatorHomeValueStep1FormFields}
                id={'houseNumber'}
                register={register}
                validation={{
                  required: validationMessagesTranslated(intl).required,
                  min: { value: 0, message: validationMessagesTranslated(intl).min(0) }
                }}
                errors={errors}
                values={watch()}
                options={{ required: true }}
              />
            </div>
            {extensions.length > 1 && (
              <div className={`md:basis-1/5`}>
                <DynamicInput
                  id={calculatorHomeValueStep1FormFields.houseNumberExt.fieldName}
                  label={calculatorHomeValueStep1FormFields.houseNumberExt.label}
                  type={calculatorHomeValueStep1FormFields.houseNumberExt.type}
                  error={errors.houseNumberExt}
                  defaultValue={calculatorHomeValueStep1FormFields.houseNumberExt.defaultValue}
                  options={extensions}
                  register={register}
                />
              </div>
            )}
          </div>
          {error && <small className={`text-red-500 text-[14px]`}>{error}</small>}
          {/*busy && (
            <small className={`text-red-500 text-[14px]`}>
              <FormattedMessage id={`step1.getAdressInformation.busy`} defaultMessage={``} />
            </small>
          )*/}
          <StepperSubmit
            id={id}
            isLoading={busy || isLoading}
            isValidated={isValid && extensions.length > 0 && !busy}
          />
        </form>
      </StepperContent>
    </Fragment>
  );
};
